import { useEffect, useState } from 'react';
import internationalization from 'src/i18n/i18n';
import type { Period, SalesRep } from './DashboardTypes';

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));
const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const dows = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const tr = (key: string, options?: any) => internationalization.t(key, options) as string;

export const DASHBOARD_SALES_REPS: SalesRep[] = [
  { id: 'repRobert', name: 'Robert' },
  { id: 'repSarah', name: 'Sarah' },
  { id: 'repMichael', name: 'Michael' },
  { id: 'repJessica', name: 'Jessica' },
];

export const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState<boolean>(() => (typeof window === 'undefined' ? false : window.innerWidth < breakpoint));

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);

  return isMobile;
};

export const zeroes = (n: number) => Array.from({ length: n }, () => 0);
export const sumArray = (arr: number[]) => arr.reduce((s, v) => s + (v || 0), 0);

const ymd = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const monthKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

const addDays = (d: Date, n: number) => {
  const dd = new Date(d);
  dd.setDate(dd.getDate() + n);
  return dd;
};

const addMonths = (d: Date, n: number) => {
  const dd = new Date(d);
  dd.setMonth(dd.getMonth() + n);
  return dd;
};

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

const normalizeDate = (value: string | Date) => {
  const dt = new Date(value);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const isoWeekOf = (date: Date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((+d - +yearStart) / 86400000 + 1) / 7);
};

export const daysInMonthForLabel = (labelYYYYMM: string): number => {
  const [yStr, mStr] = labelYYYYMM.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  return new Date(y, m, 0).getDate();
};

const formatDayDate = (d: Date) => `${tr(dows[d.getDay()])} ${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
const formatMonYY = (d: Date) => `${tr(months[d.getMonth()])} ${String(d.getFullYear() % 100).padStart(2, '0')}`;

const MONTH_WEEK_BUCKET_DAYS = 7;
const MONTH_WEEK_BUCKET_COUNT = 4;
const MONTH_TOTAL_DAYS = MONTH_WEEK_BUCKET_DAYS * MONTH_WEEK_BUCKET_COUNT;

const buildMonthWeekBucketLabel = (bucketStart: Date, bucketEnd: Date) => `${ymd(bucketStart)}__${ymd(bucketEnd)}`;

const parseMonthWeekBucketLabel = (label: string) => {
  const [start, end] = label.split('__');
  if (!start || !end) return null;
  return { start: normalizeDate(start), end: normalizeDate(end) };
};

export const getRollingDashboardLabels = (period: Period, clock: Date = new Date()): string[] => {
  const now = normalizeDate(clock);

  if (period === 'Week') {
    const start = addDays(now, -6);
    return Array.from({ length: 7 }, (_, i) => ymd(addDays(start, i)));
  }

  if (period === 'Month') {
    const start = addDays(now, -(MONTH_TOTAL_DAYS - 1));
    return Array.from({ length: MONTH_WEEK_BUCKET_COUNT }, (_, i) => {
      const bucketStart = addDays(start, i * MONTH_WEEK_BUCKET_DAYS);
      const bucketEnd = addDays(bucketStart, MONTH_WEEK_BUCKET_DAYS - 1);
      return buildMonthWeekBucketLabel(bucketStart, bucketEnd);
    });
  }

  const currentMonthStart = startOfMonth(now);
  const startMonth = addMonths(currentMonthStart, -11);
  return Array.from({ length: 12 }, (_, i) => monthKey(addMonths(startMonth, i)));
};

export const getRollingDashboardLabelForDate = (value: string | Date, period: Period, labels: string[]): string => {
  const dt = normalizeDate(value);

  if (period === 'Year') {
    const key = monthKey(dt);
    return labels.includes(key) ? key : '';
  }

  if (period === 'Month') {
    const matched = labels.find(label => {
      const range = parseMonthWeekBucketLabel(label);
      if (!range) return false;
      return dt >= range.start && dt <= range.end;
    });
    return matched ?? '';
  }

  const key = ymd(dt);
  return labels.includes(key) ? key : '';
};

export const getDisplayLabels = (period: Period, labels: string[]): string[] => {
  if (period === 'Week') {
    return labels.map(key => {
      const [y, m, d] = key.split('-').map(Number);
      const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
      const day = tr(dows[dt.getDay()]);
      return `${day} ${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}`;
    });
  }

  if (period === 'Month') {
    return labels.map(label => {
      const range = parseMonthWeekBucketLabel(label);
      if (!range) return label;
      return `${pad2(range.start.getDate())}/${pad2(range.start.getMonth() + 1)}–${pad2(range.end.getDate())}/${pad2(range.end.getMonth() + 1)}`;
    });
  }

  return labels.map(k => {
    const [yy, mm] = k.split('-').map(Number);
    const mon = tr(months[Math.max(0, Math.min(11, (mm ?? 1) - 1))]);
    const shortY = String(yy % 100).padStart(2, '0');
    return `${mon} ${shortY}`;
  });
};

export const buildRangeTitle = (period: Period): string => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  if (period === 'Week') {
    const start = addDays(now, -6);
    const end = now;
    const wStart = isoWeekOf(start);
    const wEnd = isoWeekOf(end);
    return `${formatDayDate(start)} (W${String(wStart).padStart(2, '0')}) – ${formatDayDate(end)} (W${String(wEnd).padStart(2, '0')})`;
  }

  if (period === 'Month') {
    const start = addDays(now, -(MONTH_TOTAL_DAYS - 1));
    return `${formatDayDate(start)} – ${formatDayDate(now)}`;
  }

  const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  return `${formatMonYY(start)} – ${formatMonYY(end)}`;
};

export const periodCopy = (period: Period) => {
  if (period === 'Week') return { windowHeader: tr('Last 7 Days'), currentLabel: tr('Today') };
  if (period === 'Month') return { windowHeader: tr('Last 4 Weeks'), currentLabel: tr('This week') };
  return { windowHeader: tr('Last 12 Months'), currentLabel: tr('This month') };
};

export const fmtInt = (n: number) => Math.round(n).toLocaleString('sv-SE');
export const fmtMoney = (n: number) => `${Math.round(n).toLocaleString('sv-SE')} kr`;